import { useEffect, useState } from 'react';
import { X, Loader2, MessageSquare, CheckCircle2, ExternalLink, Pencil, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { OWNER_LABELS, type GridBlock, type GridCell } from '../utils/discoveryGrid';
import { BLOCKED_FILL, LEVEL_FILL } from './DiscoveryGrid';
import { askFollowUp, checkCell, descopeCell, getCellThread, getConfigItem, getGuidedQuestions, getOpenDraftChangeOrder, replyFollowUp, restoreCell, saveGuidedAnswer, unlockConfigItem, type CellThread, type DescopeDestination, type GuidedQuestionDef } from '../utils/discoveryGridApi';
import { Checkbox } from './ui/checkbox';
import type { CellChange } from '../utils/discoveryGridPatch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { ConfigItemAssignmentDialog, type AssignableItem } from './ConfigItemAssignmentDialog';
import { CILockedBanner } from './CILockedBanner';
import { CIUnlockModal } from './CIUnlockModal';
import { CIStatusHistory } from './CIStatusHistory';
import { GridDecisionPanel } from './decision/GridDecisionPanel';

/**
 * The cell drawer (DISCOVERY_GRID_REDESIGN_SPEC §4.8): the cell's facts, its
 * answer and follow-up thread, and one primary action per level and role.
 * Consultant: 0 open to answer or assign · 1 confirm the suggestion ·
 * 2 waiting on the client · 3 check the answer · 4 ask a follow-up · 5 none.
 * Client: 0 answer · 1 confirm or edit · 2 reply · 3/4 view · 5 none.
 *
 * Config-item blocks also carry who on the client side owns the item (ledger
 * L21): everyone sees it, a consultant assigns or reassigns it from here.
 */

export interface DiscoveryCellDrawerProps {
  companyId: string;
  engagementId?: string | null;
  accessToken: string;
  viewerRole: 'consultant' | 'client';
  cell: GridCell;
  block: GridBlock;
  category: string;
  onClose: () => void;
  /** Open the full surface (review panel, guided mode, wizard or route). */
  onOpenWork: (cell: GridCell, block: GridBlock, opts?: { edit?: boolean }) => void;
  /** An action succeeded: the host applies the expected change at once and reloads the grid. */
  onChanged: (change: CellChange) => void;
  /** True while the host reloads the grid behind this drawer; actions wait for it. */
  refreshing?: boolean;
  /** Who is answering, recorded on an edited guided answer. */
  viewer?: { id?: string; name?: string };
}

function fmt(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function answerText(answer: unknown): string {
  if (answer === undefined || answer === null || answer === '') return '';
  if (typeof answer === 'string') return answer;
  if (Array.isArray(answer)) return answer.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(', ');
  if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
  return JSON.stringify(answer);
}

export function DiscoveryCellDrawer({ companyId, engagementId, accessToken, viewerRole, cell, block, category, onClose, onOpenWork, onChanged, refreshing = false, viewer }: DiscoveryCellDrawerProps) {
  const [thread, setThread] = useState<CellThread | null>(null);
  const [busy, setBusy] = useState(false);
  const [composer, setComposer] = useState<'none' | 'follow-up' | 'reply' | 'descope' | 'edit'>('none');
  // Inline answer editing for guided cells: the question's definition (type, options) and the draft value.
  const [questionDef, setQuestionDef] = useState<GuidedQuestionDef | null>(null);
  const [editValue, setEditValue] = useState<unknown>(null);
  const [loadingDef, setLoadingDef] = useState(false);
  const [text, setText] = useState('');
  const [descopeTo, setDescopeTo] = useState<DescopeDestination>('engagement');
  const [descopeRef, setDescopeRef] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  // The lock UI (ledger L16, L45): the item behind the block, loaded when the cell is locked or the
  // history is asked for; the consultant's unlock dialog.
  const [item, setItem] = useState<any | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [openDraftCo, setOpenDraftCo] = useState<{ id: string; changeOrderId: string } | null>(null);

  // When the unlock modal opens on an engagement, learn whether a draft change order is open:
  // the checkbox then reads "Add to open change order CO-…" instead of "Create change order".
  useEffect(() => {
    if (!unlockOpen || !engagementId) { setOpenDraftCo(null); return; }
    let live = true;
    getOpenDraftChangeOrder(engagementId, accessToken).then((co) => { if (live) setOpenDraftCo(co); }).catch(() => { if (live) setOpenDraftCo(null); });
    return () => { live = false; };
  }, [unlockOpen, engagementId, accessToken]);
  const [showHistory, setShowHistory] = useState(false);
  const isQuestion = cell.kind === 'question';
  const isConsultant = viewerRole === 'consultant';
  const isDescoped = !!cell.descoped;
  const canEditInline = isQuestion && !!block.guided && !!cell.questionId && !isDescoped;
  // Guided-question blocks have no config item behind them, so nothing to assign.
  const assignable: AssignableItem | null = block.guided ? null : { id: block.configItemId, name: block.name, assignedTo: block.assignedTo, assignedToName: block.assignedToName };
  // Level 5 is "Locked": the item's lifecycle status is ready_for_build or after (configItemLifecycle.isLocked).
  const isLocked = isQuestion && !block.guided && !isDescoped && cell.level === 5;

  useEffect(() => {
    let cancelled = false;
    setItem(null);
    setShowHistory(false);
    setUnlockOpen(false);
    if (block.guided || !(isLocked)) return;
    getConfigItem(companyId, block.configItemId, accessToken)
      .then((it) => { if (!cancelled) setItem(it); })
      .catch(() => { /* the banner still shows; the lock facts are optional */ });
    return () => { cancelled = true; };
  }, [companyId, block.configItemId, block.guided, isLocked, accessToken]);

  const openHistory = async () => {
    setShowHistory(true);
    if (item || block.guided) return;
    try { setItem(await getConfigItem(companyId, block.configItemId, accessToken)); } catch (e: any) { toast.error(e?.message || 'Could not load the history'); }
  };

  const unlock = async (reason: string, createChangeOrder: boolean) => {
    setUnlocking(true);
    try {
      const { changeOrder } = await unlockConfigItem(companyId, block.configItemId, { reason, createChangeOrder, engagementId: engagementId ?? null }, accessToken);
      setUnlockOpen(false);
      toast.success(changeOrder
        ? (changeOrder.action === 'added' ? `Unlocked and added to ${changeOrder.changeOrderId}` : `Unlocked; ${changeOrder.changeOrderId} drafted`)
        : 'Unlocked: discovery is open again for this item');
      onChanged({ kind: 'unlocked' });
    } catch (e: any) {
      toast.error(e?.message || 'Could not unlock this item');
    } finally {
      setUnlocking(false);
    }
  };
  const historyEntries = (item?.statusHistory?.length ?? 0) + (item?.unlockHistory?.length ?? 0);

  /** Open the inline editor: load the category's question definitions (type, options), seed the draft with the current answer. */
  const openEditor = async () => {
    if (!cell.questionId) return;
    setLoadingDef(true);
    try {
      const defs = await getGuidedQuestions(companyId, category, accessToken);
      const def = defs.find((q) => q.id === cell.questionId) ?? { id: cell.questionId, questionText: cell.text, questionType: typeof cell.answer === 'boolean' ? 'boolean' : Array.isArray(cell.answer) ? 'checkbox' : 'textarea' };
      setQuestionDef(def);
      setEditValue(cell.answer ?? (def.questionType === 'checkbox' || def.questionType === 'list_builder' ? [] : ''));
      setComposer('edit');
    } catch (e: any) {
      toast.error(e?.message || 'Could not load the question');
    } finally {
      setLoadingDef(false);
    }
  };

  const saveEdit = () => run(
    () => saveGuidedAnswer(companyId, category, { questionId: cell.questionId!, question: cell.text, answer: editValue, answeredBy: viewer?.id, answeredByName: viewer?.name }, accessToken).then(() => getCellThread(companyId, cell.id, accessToken)),
    'Answer saved',
    { kind: 'answered', answer: editValue },
  );

  useEffect(() => {
    let cancelled = false;
    setThread(null);
    setComposer('none');
    setText('');
    setDescopeRef('');
    setQuestionDef(null);
    setEditValue(null);
    if (!isQuestion) return;
    getCellThread(companyId, cell.id, accessToken)
      .then((t) => { if (!cancelled) setThread(t); })
      .catch(() => { if (!cancelled) setThread({ cellId: cell.id, followUp: null, checked: null }); });
    return () => { cancelled = true; };
  }, [companyId, cell.id, accessToken, isQuestion]);

  const run = async (fn: () => Promise<CellThread>, okMessage: string, change: CellChange) => {
    try {
      setBusy(true);
      const t = await fn();
      setThread(t);
      setComposer('none');
      setText('');
      toast.success(okMessage);
      onChanged(change);
    } catch (e: any) {
      toast.error(e?.message || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const runScope = async (fn: () => Promise<any>, okMessage: string, change: CellChange) => {
    try {
      setBusy(true);
      await fn();
      setComposer('none');
      setText('');
      toast.success(okMessage);
      onChanged(change);
    } catch (e: any) {
      toast.error(e?.message || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const fill = isDescoped ? '#FFFFFF' : cell.blocked ? BLOCKED_FILL : LEVEL_FILL[cell.level];
  const messages = thread?.followUp?.messages ?? [];
  const followUpOpen = !!thread?.followUp?.open;

  // Primary action by level and role (spec §4.8)
  let primary: { label: string; onClick: () => void; disabled?: boolean } | null = null;
  if (isDescoped) {
    primary = isConsultant ? { label: 'Bring back into scope', onClick: () => runScope(() => restoreCell(companyId, cell.id, accessToken), 'Back in scope', { kind: 'restored' }) } : null;
  } else if (cell.blocked) {
    primary = { label: isConsultant ? 'Resolve the blocker' : 'See what is blocking this', onClick: () => onOpenWork(cell, block) };
  } else if (!isQuestion) {
    primary = { label: cell.level >= 4 ? 'Open the artifact' : 'Open the wizard', onClick: () => onOpenWork(cell, block) };
  } else if (isConsultant) {
    switch (cell.level) {
      case 0: primary = { label: 'Open to answer or assign', onClick: () => onOpenWork(cell, block) }; break;
      case 1: primary = { label: 'Confirm the suggestion', onClick: () => onOpenWork(cell, block) }; break;
      case 2: primary = { label: "Waiting on the client's reply", onClick: () => {}, disabled: true }; break;
      case 3: primary = { label: 'Mark as checked', onClick: () => run(() => checkCell(companyId, cell.id, accessToken), 'Checked', { kind: 'checked' }) }; break;
      case 4: primary = { label: 'Ask a follow-up', onClick: () => setComposer('follow-up') }; break;
      default: primary = null;
    }
  } else {
    switch (cell.level) {
      case 0: primary = { label: 'Answer', onClick: () => onOpenWork(cell, block) }; break;
      case 1: primary = { label: 'Confirm or edit the suggestion', onClick: () => onOpenWork(cell, block) }; break;
      case 2: primary = { label: 'Reply to the follow-up', onClick: () => setComposer('reply') }; break;
      case 3: case 4: primary = canEditInline ? { label: 'Edit the answer', onClick: openEditor, disabled: loadingDef } : { label: 'View or edit the answer', onClick: () => onOpenWork(cell, block) }; break;
      default: primary = null;
    }
  }

  return (
    <aside className="w-full md:flex-[3_1_360px] md:min-w-[360px] md:max-w-[min(760px,55%)] bg-white border-l border-gray-200 p-5 flex flex-col gap-4 overflow-y-auto" aria-label="Selected cell">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] text-gray-500">{formatCategoryLabel(category)} · {block.name}</div>
        <Button variant="ghost" size="icon" className="h-7 w-7 -mr-2 -mt-1" onClick={onClose} aria-label="Close"><X className="size-4" /></Button>
      </div>

      <div className="flex items-center gap-2.5">
        <span className={`inline-block w-5 h-5 shrink-0 ${cell.kind === 'artifact' ? 'rounded-full' : 'rounded-[3px]'}`} style={{ backgroundColor: fill, border: isDescoped ? '1.5px solid #9CA3AF' : undefined, boxSizing: 'border-box' }} />
        <span className="text-sm font-semibold text-gray-900">{isDescoped ? 'Descoped' : cell.levelLabel}</span>
        <span className="text-xs text-gray-500">{cell.kind === 'artifact' ? 'Artifact cell' : block.guided ? 'Guided question' : 'Question cell'}</span>
      </div>

      <h3 className="text-lg font-semibold leading-snug text-gray-900">{cell.text}</h3>

      {isLocked && (
        <div data-testid="cell-locked">
          <CILockedBanner
            lockedBy={item?.lockedBy}
            lockedAt={item?.lockedAt || item?.statusUpdatedAt || item?.lastModified}
            lockedReason={item?.lockedReason || (block.status ? `Status: ${String(block.status).replace(/[_-]/g, ' ')}` : undefined)}
            userRole={isConsultant ? 'consultant' : 'user'}
            onUnlock={isConsultant ? () => setUnlockOpen(true) : undefined}
          />
          {isConsultant && (
            <CIUnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} onConfirm={unlock} ciName={block.name} loading={unlocking} openChangeOrder={openDraftCo} />
          )}
        </div>
      )}

      {assignable && (
        <div className="flex items-center justify-between gap-2 text-xs text-gray-600" data-testid="cell-assignment">
          <span>
            {assignable.assignedTo
              ? <>Client owner of this item: <span className="font-medium text-gray-900">{assignable.assignedToName || assignable.assignedTo}</span></>
              : 'No client owner for this item yet'}
          </span>
          {isConsultant && (
            <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => setAssignOpen(true)}>
              <UserPlus className="size-3.5 mr-1" aria-hidden="true" />{assignable.assignedTo ? 'Reassign' : 'Assign'}
            </Button>
          )}
        </div>
      )}

      {isDescoped && cell.descoped && (
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg px-3 py-2 text-xs leading-relaxed">
          <div className="font-semibold">{cell.descoped.label ?? cell.descoped.to}{cell.descoped.ref ? ` (${cell.descoped.ref})` : ''}</div>
          <div className="text-blue-800/80">{fmt(cell.descoped.at)}{cell.descoped.byName ? ` · ${cell.descoped.byName}` : ''}{cell.descoped.reason ? ` · ${cell.descoped.reason}` : ''}</div>
          <div className="mt-1 text-blue-800/70">Not counted in this engagement's progress. Its answer and position are kept.</div>
        </div>
      )}

      {cell.blocked && cell.gatingIssues.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2 text-xs leading-relaxed">
          <div className="font-semibold mb-1">Blocked by</div>
          <ul className="list-disc pl-4 space-y-0.5">{cell.gatingIssues.slice(0, 4).map((g, i) => <li key={i}>{g.message}</li>)}</ul>
        </div>
      )}

      {isQuestion && cell.answer !== undefined && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 leading-relaxed">
          <div className="text-[11px] text-gray-500 mb-1">
            {cell.level === 1 ? 'Suggested by AI, not yet confirmed' : `Answer${cell.answeredBy ? ` from ${cell.answeredBy}` : ''}${cell.answeredAt ? `, ${fmt(cell.answeredAt)}` : ''}`}
            {thread?.checked && !followUpOpen ? ` · checked by ${thread.checked.byName || 'a consultant'} ${fmt(thread.checked.at)}` : ''}
          </div>
          <div className="whitespace-pre-wrap break-words">{answerText(cell.answer)}</div>
        </div>
      )}

      {/* An architecture choice that departs from best practice: the client's decision card (RESTORE_PLAN R2.7) */}
      {isQuestion && cell.answer !== undefined && !isDescoped && (
        <GridDecisionPanel
          companyId={companyId}
          cell={cell}
          accessToken={accessToken}
          viewerRole={viewerRole}
          viewer={viewer}
          onEditAnswer={canEditInline ? () => { void openEditor(); } : () => onOpenWork(cell, block, { edit: true })}
        />
      )}

      {messages.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><MessageSquare className="size-3" /> Follow-up thread{followUpOpen ? ' · waiting on the client' : ''}</div>
          {messages.map((m, i) => (
            <div key={i} className={`rounded-lg px-3 py-2 text-sm leading-relaxed border ${m.role === 'consultant' ? 'bg-white border-sky-200 border-l-[3px] border-l-sky-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="text-[11px] text-gray-500 mb-0.5">{m.byName || (m.role === 'consultant' ? 'Consultant' : 'Client')} · {fmt(m.at)}</div>
              <div className="whitespace-pre-wrap break-words text-gray-800">{m.text}</div>
            </div>
          ))}
        </div>
      )}

      {composer === 'descope' && (
        <div className="flex flex-col gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-gray-700">Take this cell out of the engagement</div>
          <Select value={descopeTo} onValueChange={(v) => setDescopeTo(v as DescopeDestination)}>
            <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="engagement">Move to a later engagement</SelectItem>
              <SelectItem value="change_order">Move to a change order</SelectItem>
              <SelectItem value="client">Hand to the client's team</SelectItem>
              <SelectItem value="dropped">Drop (reason required)</SelectItem>
            </SelectContent>
          </Select>
          {(descopeTo === 'engagement' || descopeTo === 'change_order') && (
            <Input value={descopeRef} onChange={(e) => setDescopeRef(e.target.value)} placeholder={descopeTo === 'change_order' ? 'Change order ref, e.g. CO-2026-003' : 'Engagement or phase name'} className="h-8 text-xs bg-white" />
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={descopeTo === 'dropped' ? 'Why is this out of scope?' : 'Reason (optional)'} className="text-sm bg-white" />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || (descopeTo === 'dropped' && !text.trim())} onClick={() => runScope(() => descopeCell(companyId, cell.id, { to: descopeTo, ref: descopeRef.trim() || undefined, reason: text.trim() || undefined, engagementId }, accessToken), 'Descoped', { kind: 'descoped', descoped: { to: descopeTo, ref: descopeRef.trim() || undefined, reason: text.trim() || undefined, at: new Date().toISOString(), by: '', fromEngagementId: engagementId ?? null } })}>
              {busy ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}Descope
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setComposer('none'); setText(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {(composer === 'follow-up' || composer === 'reply') && (
        <div className="flex flex-col gap-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={composer === 'follow-up' ? 'What else do you need to know?' : 'Your reply'} className="text-sm" />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !text.trim()} onClick={() => run(() => (composer === 'follow-up' ? askFollowUp(companyId, cell.id, text, accessToken) : replyFollowUp(companyId, cell.id, text, accessToken, engagementId)), composer === 'follow-up' ? 'Follow-up sent to the client' : 'Reply sent', { kind: composer === 'follow-up' ? 'follow-up' : 'reply' })}>
              {busy ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}{composer === 'follow-up' ? 'Send follow-up' : 'Send reply'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setComposer('none'); setText(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {composer === 'edit' && questionDef && (
        <div className="flex flex-col gap-2 bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Edit the answer</div>
          {questionDef.helpText && <div className="text-xs text-gray-500">{questionDef.helpText}</div>}
          <AnswerEditor def={questionDef} value={editValue} onChange={setEditValue} />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || refreshing} onClick={saveEdit}>
              {busy ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}Save answer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setComposer('none')}>Cancel</Button>
          </div>
          {isConsultant && <div className="text-[11px] text-gray-500">Saving records the answer as new: it returns to Answered and is unchecked until you look at it again.</div>}
        </div>
      )}

      <dl className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-gray-500">Ball in court</dt><dd className="text-gray-900 font-medium">{OWNER_LABELS[cell.owner]}</dd>
        <dt className="text-gray-500">Assigned to</dt><dd className="text-gray-900">{block.assignedToName || (block.guided ? 'Whole category' : 'Unassigned')}</dd>
        {!block.guided && <><dt className="text-gray-500">Config item</dt><dd className="text-gray-900">{block.name}{block.status ? ` (${String(block.status).replace(/_/g, ' ')})` : ''}</dd></>}
        <dt className="text-gray-500">Source</dt><dd className="text-gray-900">{cell.source.label || 'Base SOW'}</dd>
      </dl>

      {!block.guided && (
        <div className="flex flex-col gap-2" data-testid="cell-history">
          {!showHistory ? (
            <button type="button" className="self-start text-xs text-[#0B4F6C] hover:underline" onClick={openHistory}>Show status and unlock history</button>
          ) : historyEntries > 0 ? (
            <CIStatusHistory statusHistory={item?.statusHistory} unlockHistory={item?.unlockHistory} />
          ) : (
            <div className="text-xs text-gray-500">{item ? 'No status changes or unlocks recorded for this item yet.' : 'Loading the history…'}</div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 mt-1">
        {refreshing && !busy && (
          <div className="flex items-center gap-2 text-xs text-gray-500" role="status"><Loader2 className="size-3.5 animate-spin" /> Updating the grid…</div>
        )}
        {primary && (
          <Button onClick={primary.onClick} disabled={busy || refreshing || primary.disabled} className="bg-[#0B4F6C] hover:bg-[#0a4660] text-white">
            {busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : cell.level === 3 && isConsultant && isQuestion ? <CheckCircle2 className="size-4 mr-2" /> : null}
            {primary.label}
          </Button>
        )}
        {isConsultant && isQuestion && cell.level === 3 && (
          <Button variant="outline" onClick={() => setComposer('follow-up')} disabled={busy || refreshing}>Ask a follow-up instead</Button>
        )}
        {isConsultant && isQuestion && cell.level === 2 && (
          <Button variant="outline" onClick={() => setComposer('follow-up')} disabled={busy || refreshing}>Add to the follow-up</Button>
        )}
        {canEditInline && cell.level >= 3 && composer !== 'edit' && (
          <Button variant="ghost" className="text-gray-600" onClick={openEditor} disabled={busy || refreshing || loadingDef}>{loadingDef ? <Loader2 className="size-3.5 mr-2 animate-spin" /> : <Pencil className="size-3.5 mr-2" />}Edit the answer</Button>
        )}
        {isQuestion && !isDescoped && (cell.level >= 1 || isConsultant) && !(block.guided && cell.level >= 3) && (
          <Button variant="ghost" className="text-gray-600" onClick={() => onOpenWork(cell, block)}><ExternalLink className="size-3.5 mr-2" />{block.guided ? 'Open in Guided Mode' : cell.level >= 3 ? 'Open or edit the full answer' : 'Open the full question'}</Button>
        )}
        {isConsultant && !isDescoped && composer !== 'descope' && (
          <Button variant="ghost" className="text-gray-500" onClick={() => setComposer('descope')} disabled={busy}>Descope this cell…</Button>
        )}
      </div>
      {assignable && isConsultant && (
        <ConfigItemAssignmentDialog
          item={assignable}
          companyId={companyId}
          accessToken={accessToken}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          onUpdate={() => onChanged({ kind: 'assigned' })}
        />
      )}
    </aside>
  );
}

/** One control per guided question type; values match what Guided Mode stores. */
function AnswerEditor({ def, value, onChange }: { def: GuidedQuestionDef; value: unknown; onChange: (v: unknown) => void }) {
  // Keep any stored value the current option list no longer offers (older question versions,
  // custom entries) so saving never silently drops it.
  const stored = Array.isArray(value) ? (value as unknown[]).filter((v): v is string => typeof v === 'string') : typeof value === 'string' && value ? [value] : [];
  const options = [...(def.options ?? []), ...stored.filter((v) => !(def.options ?? []).includes(v))];
  switch (def.questionType) {
    case 'boolean':
      return (
        <div className="flex gap-2" role="radiogroup" aria-label={def.questionText}>
          {[true, false].map((v) => (
            <Button key={String(v)} size="sm" variant={value === v ? 'default' : 'outline'} role="radio" aria-checked={value === v} onClick={() => onChange(v)}>{v ? 'Yes' : 'No'}</Button>
          ))}
        </div>
      );
    case 'select':
    case 'multiple_choice':
      return (
        <div className="flex flex-col gap-1" role="radiogroup" aria-label={def.questionText}>
          {options.map((opt) => (
            <button key={opt} type="button" role="radio" aria-checked={value === opt} onClick={() => onChange(opt)} className={`text-left text-sm px-2.5 py-1.5 rounded-md border ${value === opt ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>{opt}</button>
          ))}
          {options.length === 0 && <Input value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm" />}
        </div>
      );
    case 'checkbox':
      return <CheckboxAnswer label={def.questionText} options={options} value={value} onChange={onChange} />;
    case 'number':
      return <Input type="number" value={value === null || value === undefined ? '' : String(value)} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} className="h-8 text-sm" />;
    case 'text':
      return <Input value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} placeholder={def.placeholder} className="h-8 text-sm" />;
    case 'list_builder': {
      const lines = Array.isArray(value) ? (value as string[]).join('\n') : typeof value === 'string' ? value : '';
      return <Textarea value={lines} onChange={(e) => onChange(e.target.value.split('\n').map((l) => l.trim()).filter(Boolean))} rows={4} placeholder="One item per line" className="text-sm" />;
    }
    default:
      return <Textarea value={typeof value === 'string' ? value : value === null || value === undefined ? '' : JSON.stringify(value)} onChange={(e) => onChange(e.target.value)} rows={4} placeholder={def.placeholder} className="text-sm" />;
  }
}

/**
 * Pick any of the options, or add your own (Guided Mode allows the same), e.g. an asset type the
 * list does not have (RESTORE_PLAN R1.8, from the old asset discovery questionnaire).
 */
export function CheckboxAnswer({ label, options, value, onChange }: { label: string; options: string[]; value: unknown; onChange: (v: unknown) => void }) {
  const [draft, setDraft] = useState('');
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const add = () => {
    const next = draft.trim();
    if (!next) return;
    if (options.some((o) => o.toLowerCase() === next.toLowerCase())) {
      const existing = options.find((o) => o.toLowerCase() === next.toLowerCase()) as string;
      if (!selected.includes(existing)) onChange([...selected, existing]);
    } else {
      onChange([...selected, next]);
    }
    setDraft('');
  };
  return (
    <div className="flex flex-col gap-1.5">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={selected.includes(opt)} onCheckedChange={(c) => onChange(c ? [...selected, opt] : selected.filter((x) => x !== opt))} />
          {opt}
        </label>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add your own"
          aria-label={`Add your own answer to: ${label}`}
          className="h-8 text-sm"
        />
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={!draft.trim()}>Add</Button>
      </div>
    </div>
  );
}
